import React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import DtvTypography from "@directv-digital/dds.ui.dtv-typography/";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";

export type TabProps = {
  label: string;
  inactive?: boolean;
  selected?: boolean;
  sx?: any;
};

export interface TabTrayProps {
  id?: string;
  variant?: "tray" | "boxed";
  mode?: "dark" | "light";
  tabs?: TabProps[];
  defaultSelectedTab?: number;
  styles?:any;
  ariaLabel?: string;
  handleChange?: (index: number) => void;
}

export function TabTray({ id, variant, mode, tabs, defaultSelectedTab, styles, ariaLabel, handleChange }: TabTrayProps) {
  try {
    if (!tabs || tabs.length === 0) {
      console.error("[TabTray] tabs prop missing");
      return null; // Return null if no tabs are provided
    };

    const [value, setValue] = React.useState(
      defaultSelectedTab ?? tabs?.findIndex(tab => tab?.selected === true) ?? 0
    );

    const [href, setHref] = React.useState<string | undefined>(undefined);
    React.useEffect(() => {
      setHref(window.location.href);
    }, []);

    const trayStyles = React.useMemo(() => ({
      width: "fit-content",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",

      ...(variant === "tray" ? {
        borderTop: "1px solid rgba(16, 38, 65, 0.12)",
        padding: "2px",
        borderRadius: "9999px",
        minHeight: {
          xs: styles?.minHeight || "69px",
          sm: "48px",
          md: "52px"
        },
        height: {
          xs: styles?.height || "69px",
          sm: "48px",
          md: "52px"
        },
        backgroundColor: mode === "dark" ? "rgba(140, 211, 245, 0.12)" : "rgba(16, 38, 65, 0.04)",
        ...styles,
        "& .MuiTabs-indicator": {
          height: "100% !important",
          backgroundColor: mode === "dark" ? "#8CD3F5" : "#102641",
          borderRadius: "9999px",
          zIndex: 0
        },
        "& .MuiTab-root": {
          zIndex: 1,
          padding: {
            xs: "11px 16px 11px 16px",
            sm: "11px 10px 11px 20px",
            md: "11px 24px 11px 24px"
          },
          textTransform: "none",
          width: styles?.width || {
            xs: "97px",
            sm: "fit-content",
            md: "fit-content"
          },
          maxWidth: "50%",
          minHeight: {
            xs: styles?.minHeight || "53px",
            sm: "44px",
            md: "48px"
          },
          height: {
            xs: styles?.minHeight || "53px",
            sm: "44px",
            md: "48px"
          },
          borderRadius: "9999px",
          color: mode === "dark" ? "#E4E4E4" : "#102641",
          transition: "color 100ms ease",
          "&.Mui-selected": {
            color: mode === "dark" ? "#102641 !important" : "#FFFFFF !important",
            transition: "color 80ms ease 220ms"
          },
          ":hover": {
            backgroundColor: mode === "dark" ? "rgba(140, 211, 245, 0.08)" : "rgba(16, 38, 65, 0.08)",
            color: mode === "dark" ? "#E4E4E4" : "#102641"
          },
          "&.Mui-focusVisible": {
            outline: "2px solid #1FBCF8",
            outlineOffset: "-2px",
            color: `${mode === "dark" ? "#E4E4E4" : "#747474"}`
          },
          "&.Mui-disabled": {
            color: "#8D8D8D"
          },
          ":active": {
            backgroundColor: mode === "dark" ? "rgba(31, 188, 248, 0.24)" : "rgba(16, 38, 65, 0.2)"
          }
        }
      } : {
        ...styles,
        "& .MuiTabs-indicator": {
          height: "3px !important",
          backgroundColor: mode === "dark" ? "#8CD3F5" : "#21528C"
        },
        "& .MuiTab-root": {
          borderRadius: "4px 4px 0px 0px",
          padding: {
            xs: "11px 20px 11px 20px",
            sm: "11px 32px 11px 32px",
            md: "11px 56px 11px 56px"
          },
          textTransform: "none",
          width: {
            xs: "97px",
            sm: "fit-content",
            md: "fit-content"
          },
          minHeight: {
            xs: styles?.minHeight || "48px",
            sm: "48px",
            md: "56px"
          },
          height: {
            xs: styles?.height || "48px",
            sm: "48px",
            md: "56px"
          },
          color: mode === "dark" ? "#E4E4E4" : "#102641",
          "&.Mui-selected": {
            backgroundColor:
                  mode === "dark" ? "rgba(255, 255, 255, 0.12) !important" : "#FFFFFF !important",
            opacity: 1,
            color: mode === "dark" ? "#8CD3F5 !important" : "#21528C !important",
            boxShadow: "0px 2px 20px 0px rgba(0, 0, 0, 0.08)"
          },
          ":hover": {
            backgroundColor: mode === "dark" ? "rgba(31, 188, 248, 0.16)" : "rgba(16, 38, 65, 0.08)",
            color: mode === "dark" ? "#E4E4E4" : "#102641"
          },
          "&.Mui-focusVisible": {
            outline: "2px solid #1FBCF8",
            outlineOffset: "-2px",
            color: `${mode === "dark" ? "#E4E4E4" : "#747474"}`
          },
          "&.Mui-disabled": {
            color: "#8D8D8D"
          },
          ":active": {
            backgroundColor: mode === "dark" ? "rgba(27, 169, 224, 0.24)" : "rgba(16, 38, 65, 0.12)"
          }
        }
      })
    }), [mode, styles]);

    const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
      setValue(newValue);
      handleChange?.(newValue);
    };

    return (
        <Tabs
          value={value}
          onChange={handleChangeTab}
          TabIndicatorProps={{ color: "red" }}
          sx={trayStyles}
        >
          {tabs.map((tab, index) => (
            <Tab
              {...getAnalyticsAttrs(tab?.label, 'component_tab', href)}
              id={id}
              tabIndex={0}
              disableRipple
              disabled={tab.inactive}
              aria-label={ariaLabel || `Toggle to ${tab.label}`}
              key={index}
              label={
                <DtvTypography variant="body2medium" sx={{
                  fontSize: "16px",
 lineHeight: {
 xs: "120%",
sm: "1.3em",
md: "1.3em"
},
zIndex: 2
}}>
                  {tab.label}
                </DtvTypography>
              }
              sx={tab.sx}
            />
          ))}
        </Tabs>
    );
  } catch(error) {
    console.log("There was an error with tabs", error);
    return <></>;
  }
}
